/**
 * Pin toggle for a project card or row.
 *
 * Both of those wrap their whole body in a <Link>, so this swallows the click
 * before the anchor sees it -- otherwise pinning would also navigate to the
 * editor.
 *
 * Always visible rather than revealed on hover: hover does not exist on touch,
 * and an unpinned project would have no way to be pinned there.
 */
export function PinButton({
  pinned,
  busy = false,
  onClick,
  className = '',
}: {
  pinned: boolean;
  busy?: boolean;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      disabled={busy}
      aria-pressed={pinned}
      aria-label={pinned ? 'Unpin project' : 'Pin project'}
      title={pinned ? 'Unpin project' : 'Pin project'}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }}
      className={`rounded-md p-1 transition disabled:cursor-not-allowed disabled:opacity-50 ${
        pinned ? 'text-honey-600 hover:text-honey-700' : 'text-stone-400 hover:text-stone-700'
      } ${className}`}
    >
      <PinIcon filled={pinned} />
    </button>
  );
}

/* Hand-drawn to match the other glyphs: there is no icon package, and the repo
   has decided not to add one for a handful of shapes. */
function PinIcon({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className="h-4 w-4"
    >
      <path d="M9 4h6l-.75 5.25 3 2.25v1.5H6.75v-1.5l3-2.25L9 4Z" />
      <path d="M12 13v7" fill="none" />
    </svg>
  );
}
